import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  UserPlus, Loader2, Check, Mail, Lock, User as UserIcon, Users
} from "lucide-react";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";

const JoinPage = () => {
  const { inviteId } = useParams();
  const { user, token } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pseudo, setPseudo] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/friends/invite/${inviteId}`);
        setInvite(res.data);
        if (res.data.used) setError("Cette invitation a déjà été utilisée.");
      } catch {
        setError("Invitation introuvable ou expirée.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [inviteId]);

  const handleRegister = async () => {
    if (!pseudo.trim() || !email.trim() || !password.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/friends/register`, {
        pseudo: pseudo.trim(),
        email: email.trim(),
        password: password.trim(),
        invite_id: inviteId,
      });
      localStorage.setItem("token", res.data.token);
      setRegistered(true);
      toast.success("Bienvenue sur Gusto !");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptAsExistingUser = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/friends/accept-invite/${inviteId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccepted(true);
      toast.success("Ami ajouté !");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center space-y-4">
            <img src={gustoAvatar} alt="Gusto" className="h-10 mx-auto opacity-40" />
            <p className="text-muted-foreground">{error}</p>
            <Link to="/register">
              <Button className="rounded-full">Créer un compte</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md w-full">
          <Card className="shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-primary to-red-500 p-8 text-center text-white">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}>
                <div className="w-20 h-20 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-4">
                  <Check className="w-10 h-10" />
                </div>
              </motion.div>
              <h2 className="font-heading text-2xl font-bold">Bienvenue {pseudo} !</h2>
              <p className="text-white/80 mt-2">Vous êtes maintenant ami(e) avec {invite?.inviter_name}</p>
            </div>
            <CardContent className="p-6 text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Complétez votre profil alimentaire pour que vos amis puissent cuisiner en tenant compte de vos préférences !
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/dietary-profile">
                  <Button className="w-full rounded-full gap-2">
                    Configurer mes préférences
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full rounded-full">
                    Explorer Gusto
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-orange-50 px-4">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md w-full">
          <Card className="shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-primary to-red-500 p-8 text-center text-white">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}>
                <div className="w-20 h-20 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-4">
                  <Users className="w-10 h-10" />
                </div>
              </motion.div>
              <h2 className="font-heading text-2xl font-bold">Ami ajouté !</h2>
              <p className="text-white/80 mt-2">Vous êtes maintenant ami(e) avec {invite?.inviter_name}</p>
            </div>
            <CardContent className="p-6 text-center space-y-4">
              <div className="flex flex-col gap-2">
                <Link to="/friends">
                  <Button className="w-full rounded-full gap-2">
                    <Users className="w-4 h-4" />
                    Voir mes amis
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full rounded-full">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 px-4 py-8" data-testid="join-page">
      <div className="max-w-md mx-auto space-y-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary to-red-500 flex items-center justify-center">
            <img src={gustoAvatar} alt="Gusto" className="h-12 w-auto" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-charcoal">
            {invite?.inviter_name} vous invite sur Gusto
          </h1>
          <p className="text-muted-foreground">
            {user ? "Acceptez l'invitation pour devenir amis !" : "Rejoignez gratuitement et cuisinez ensemble !"}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="shadow-xl">
            <CardContent className="p-6 space-y-4">
              {user ? (
                <>
                  <div className="text-center space-y-2 py-2">
                    <p className="text-sm text-muted-foreground">
                      Connecté en tant que <strong className="text-charcoal">{user.pseudo}</strong>
                    </p>
                  </div>
                  <Button
                    onClick={handleAcceptAsExistingUser}
                    disabled={submitting}
                    className="w-full h-12 rounded-full text-base gap-2"
                    data-testid="accept-invite-btn"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Users className="w-5 h-5" />}
                    Accepter l'invitation
                  </Button>
                </>
              ) : (
                <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Votre prénom / pseudo</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Ex: Marie"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    className="pl-10"
                    data-testid="join-pseudo-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="marie@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="join-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="Minimum 6 caractères"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="join-password-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleRegister}
                disabled={!pseudo.trim() || !email.trim() || !password.trim() || submitting}
                className="w-full h-12 rounded-full text-base gap-2"
                data-testid="join-submit-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
                Rejoindre Gusto
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Déjà un compte ? <Link to="/login" className="text-primary font-medium">Se connecter</Link>
              </p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default JoinPage;
